import React, { useState, useEffect, useCallback } from "react";
import { Modal, ModalHeader, ModalBody, ModalFooter, Button, Row, Col, Label, Input, Spinner } from "reactstrap";
import FinancialCategoryService from "../../../../services/financial_category_service";
import { useTenant } from "../../../../hooks/useTenant";
import { FINANCIAL_TYPES } from "../../../../constants/financial";

/**
 * PayableForm
 * Modal para criar uma nova despesa manual.
 */
const PayableForm = ({ isOpen, toggle, onSave, processing }) => {
    const { idTenant } = useTenant();
    const [categories, setCategories] = useState([]);
    const [loadingCategories, setLoadingCategories] = useState(true);

    const [formData, setFormData] = useState({
        description: "",
        amount: "",
        dueDate: new Date().toISOString().split("T")[0],
        competenceDate: new Date().toISOString().split("T")[0],
        recipient: "",
        idCategory: "",
        categoryName: "",
        observation: "",
    });

    // Carrega categorias do tipo EXPENSE
    useEffect(() => {
        if (!isOpen || !idTenant) return;
        setLoadingCategories(true);
        FinancialCategoryService.getAll(idTenant)
            .then(cats => {
                setCategories(cats.filter(c => c.type === FINANCIAL_TYPES.EXPENSE));
            })
            .catch(err => console.error("[PayableForm] Erro ao carregar categorias:", err))
            .finally(() => setLoadingCategories(false));
    }, [isOpen, idTenant]);

    const handleChange = useCallback((field, value) => {
        setFormData(prev => ({ ...prev, [field]: value }));
    }, []);

    const handleCategoryChange = useCallback((categoryId) => {
        const cat = categories.find(c => c.id === categoryId);
        setFormData(prev => ({
            ...prev,
            idCategory: categoryId,
            categoryName: cat?.name || "",
        }));
    }, [categories]);

    const handleSubmit = useCallback(() => {
        if (!formData.description || !formData.amount || !formData.dueDate) return;
        onSave(formData);
    }, [formData, onSave]);

    const resetForm = useCallback(() => {
        setFormData({
            description: "",
            amount: "",
            dueDate: new Date().toISOString().split("T")[0],
            competenceDate: new Date().toISOString().split("T")[0],
            recipient: "",
            idCategory: "",
            categoryName: "",
            observation: "",
        });
    }, []);

    const isValid = formData.description && formData.amount && formData.dueDate;

    return (
        <Modal isOpen={isOpen} toggle={toggle} onClosed={resetForm} size="lg" centered className="border-0 shadow">
            <ModalHeader toggle={toggle} className="border-bottom pb-3">
                <span className="fw-bold text-dark font-size-15">
                    <i className="mdi mdi-plus-circle-outline text-primary me-2" />
                    Nova Despesa
                </span>
            </ModalHeader>
            <ModalBody>
                <Row className="g-3">
                    <Col md={8}>
                        <Label className="form-label small fw-bold">Descrição *</Label>
                        <Input
                            type="text"
                            value={formData.description}
                            onChange={(e) => handleChange("description", e.target.value)}
                            placeholder="Ex: Pagamento aluguel janeiro"
                        />
                    </Col>
                    <Col md={4}>
                        <Label className="form-label small fw-bold">Valor *</Label>
                        <Input
                            type="number"
                            step="0.01"
                            min="0"
                            value={formData.amount}
                            onChange={(e) => handleChange("amount", e.target.value)}
                            placeholder="0,00"
                        />
                    </Col>
                    <Col md={4}>
                        <Label className="form-label small fw-bold">Vencimento *</Label>
                        <Input
                            type="date"
                            value={formData.dueDate}
                            onChange={(e) => handleChange("dueDate", e.target.value)}
                        />
                    </Col>
                    <Col md={4}>
                        <Label className="form-label small fw-bold">Competência</Label>
                        <Input
                            type="date"
                            value={formData.competenceDate}
                            onChange={(e) => handleChange("competenceDate", e.target.value)}
                        />
                    </Col>
                    <Col md={4}>
                        <Label className="form-label small fw-bold">Categoria</Label>
                        {loadingCategories ? (
                            <div className="py-2"><Spinner size="sm" /></div>
                        ) : (
                            <Input
                                type="select"
                                value={formData.idCategory}
                                onChange={(e) => handleCategoryChange(e.target.value)}
                            >
                                <option value="">Selecione...</option>
                                {categories.map(c => (
                                    <option key={c.id} value={c.id}>{c.name}</option>
                                ))}
                            </Input>
                        )}
                    </Col>
                    <Col md={6}>
                        <Label className="form-label small fw-bold">Fornecedor / Beneficiário</Label>
                        <Input
                            type="text"
                            value={formData.recipient}
                            onChange={(e) => handleChange("recipient", e.target.value)}
                            placeholder="Nome do fornecedor"
                        />
                    </Col>
                    <Col md={6}>
                        <Label className="form-label small fw-bold">Observação</Label>
                        <Input
                            type="text"
                            value={formData.observation}
                            onChange={(e) => handleChange("observation", e.target.value)}
                            placeholder="Nota interna (opcional)"
                        />
                    </Col>
                </Row>
            </ModalBody>
            <ModalFooter className="border-top pt-3">
                <Button color="light" className="text-muted" onClick={toggle} disabled={processing}>
                    Cancelar
                </Button>
                <Button color="primary" onClick={handleSubmit} disabled={!isValid || processing}>
                    {processing ? <><Spinner size="sm" className="me-1" /> Salvando...</> : "Salvar"}
                </Button>
            </ModalFooter>
        </Modal>
    );
};

export default PayableForm;
